import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Textarea } from '../../../foundation/altzero_base/base/components/ui/textarea';
import { Alert, AlertDescription } from '../../../foundation/altzero_base/base/components/ui/alert';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Separator } from '../../../foundation/altzero_base/base/components/ui/separator';
import { ScrollArea } from '../../../foundation/altzero_base/base/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../foundation/altzero_base/base/components/ui/command';
import {
  Save,
  Trash2,
  FileText,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Check
} from 'lucide-react';
import {
  getTemplatesWithPrompts,
  upsertTemplatePrompt,
  deleteTemplatePrompt,
  TemplateWithPrompt
} from '../services/templatePromptService';
import ScopingAILayout from './ScopingAILayout';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';

interface PromptTemplateConfigProps {
  standalone?: boolean;
}

const PromptTemplateConfig: React.FC<PromptTemplateConfigProps> = ({ standalone = false }) => {
  // State management
  const [templates, setTemplates] = useState<TemplateWithPrompt[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateWithPrompt | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [templateSearchOpen, setTemplateSearchOpen] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Load templates with prompts on component mount
  useEffect(() => {
    loadTemplatesWithPrompts();
  }, []);

  // Update current prompt when selected template changes
  useEffect(() => {
    if (selectedTemplate) {
      setCurrentPrompt(selectedTemplate.prompt?.prompt || '');
      setError(null);
      setSuccess(null);
    }
  }, [selectedTemplate]);

  const loadTemplatesWithPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await getTemplatesWithPrompts();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load templates');
      }

      const templatesData = result.data || [];
      setTemplates(templatesData);
      
      // Auto-select first template if none selected
      if (templatesData.length > 0) {
        setSelectedTemplate(templatesData[0]);
      }
    } catch (error: any) {
      console.error('Error loading templates:', error);
      setError(error.message || 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    if (!currentPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const result = await upsertTemplatePrompt(
        selectedTemplate.id,
        currentPrompt.trim()
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save prompt');
      }

      // Update the template in local state
      setTemplates(prev => prev.map(template => 
        template.id === selectedTemplate.id 
          ? { ...template, prompt: result.data }
          : template
      ));

      // Update selected template
      setSelectedTemplate(prev => prev ? { ...prev, prompt: result.data } : null);

      setSuccess('Prompts saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error saving prompt:', error);
      setError(error.message || 'Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePrompt = async () => {
    if (!selectedTemplate || !selectedTemplate.prompt) {
      setError('No prompt to delete');
      return;
    }

    if (!confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      setSuccess(null);

      const result = await deleteTemplatePrompt(selectedTemplate.id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete prompt');
      }

      // Update the template in local state
      setTemplates(prev => prev.map(template => 
        template.id === selectedTemplate.id 
          ? { ...template, prompt: undefined }
          : template
      ));

      // Update selected template and clear prompts
      setSelectedTemplate(prev => prev ? { ...prev, prompt: undefined } : null);
      setCurrentPrompt('');

      setSuccess('Prompt deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error deleting prompt:', error);
      setError(error.message || 'Failed to delete prompt');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTemplateSelect = (template: TemplateWithPrompt) => {
    setSelectedTemplate(template);
    setTemplateSearchOpen(false);
    setTemplateSearchQuery('');
  };

  // Filter templates based on search query
  const filteredTemplates = templates.filter((template) => {
    if (!templateSearchQuery.trim()) return true;
    const query = templateSearchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  });

  if (isLoading) {
    const loadingContent = (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
    return standalone ? loadingContent : <ScopingAILayout>{loadingContent}</ScopingAILayout>;
  }

  const content = (
      <div className="flex flex-col h-[calc(100vh-3rem)] w-full -m-4 md:-m-6">
        {/* Header with Template Selector */}
        <div className="p-6 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Prompt Template Configuration</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {templates.length} template{templates.length !== 1 ? 's' : ''} available
              </p>
            </div>
          </div>

          {/* Searchable Template Dropdown */}
          <div className="w-full max-w-2xl">
            <Popover open={templateSearchOpen} onOpenChange={setTemplateSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={templateSearchOpen}
                  className="w-full justify-between h-auto py-3"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedTemplate ? (
                      <>
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium truncate">{selectedTemplate.name}</p>
                          {selectedTemplate.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {selectedTemplate.description}
                            </p>
                          )}
                        </div>
                        {selectedTemplate.prompt && (
                          <Badge variant="secondary" className="flex-shrink-0 text-xs">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Has Prompt
                          </Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">Select a template...</span>
                    )}
                  </div>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                <Command shouldFilter={false}>
                  <CommandInput 
                    placeholder="Search templates..." 
                    value={templateSearchQuery}
                    onValueChange={setTemplateSearchQuery}
                  />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No templates found.</CommandEmpty>
                    <CommandGroup>
                      {filteredTemplates.map((template) => (
                        <CommandItem
                          key={template.id}
                          value={`${template.name} ${template.description || ''}`}
                          onSelect={() => handleTemplateSelect(template)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-start gap-2 flex-1 min-w-0 w-full">
                            <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{template.name}</p>
                                {template.prompt && (
                                  <Badge variant="secondary" className="text-xs">
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    Has Prompt
                                  </Badge>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                  {template.description}
                                </p>
                              )}
                            </div>
                            <Check
                              className={cn(
                                "h-4 w-4 flex-shrink-0",
                                selectedTemplate?.id === template.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Error/Success Messages */}
          <div className="px-6 pt-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Prompt Configuration */}
          <ScrollArea className="flex-1">
            <div className="p-6">
              {selectedTemplate ? (
                <div className="space-y-6 max-w-full">
                  {/* Custom Prompt */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Custom Prompt
                      </CardTitle>
                      <CardDescription>
                        Define the custom prompt for this template. This prompt will be used when processing documents with this template.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={currentPrompt}
                        onChange={(e) => setCurrentPrompt(e.target.value)}
                        placeholder="Enter custom prompt for this template...

Example:
You are processing a tender proposal template. Focus on:
1. Populate all tables with accurate contract data
2. Use 12-month contract terms when specified
3. Format currency as $XX,XXX
4. Include all site information in site tables
5. Ensure offer comparisons are clear and accurate

Be precise and use only the provided data."
                        className="min-h-[300px] font-mono text-sm w-full"
                        disabled={!selectedTemplate}
                      />
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      onClick={handleSavePrompt}
                      disabled={isSaving || !currentPrompt.trim()}
                      className="flex items-center gap-2"
                      size="lg"
                    >
                      <Save className="h-4 w-4" />
                      {isSaving ? 'Saving...' : 'Save Prompts'}
                    </Button>

                    {selectedTemplate.prompt && (
                      <Button 
                        variant="destructive"
                        onClick={handleDeletePrompt}
                        disabled={isDeleting}
                        className="flex items-center gap-2"
                        size="lg"
                      >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete Prompts'}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-20 text-muted-foreground">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No template selected</p>
                  <p className="text-sm mt-2">Select a template from the sidebar to configure prompts</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
  );

  return standalone ? content : <ScopingAILayout>{content}</ScopingAILayout>;
};

export default PromptTemplateConfig;
